// The save contract of OPS's scanner leg (ops/revival-scan-truth b0334733),
// spoken the same way as the mobile scanner (A, mobile-suite-v2 1e9b12d):
// - work_status is required and has no default: the office answers it, the scan
//   never guesses. Quote.status follows it: completed → completed,
//   not completed → sent, unknown → draft (paper_truth.QUOTE_STATUS_FOR_WORK).
// - "Date on ticket" is the date written on the paper, sent as document_date —
//   never the upload day. Empty is a valid answer (the paper has none).
// - A 409 customer_decision_required means the ticket's phone/email belongs to
//   a customer at another address; nothing was written, the reviewer picks.

export const WORK_STATUS_OPTIONS = [
  { value: "completed", label: "Completed" },
  { value: "not_completed", label: "Not completed" },
  { value: "unknown", label: "Unknown" },
];

const QUOTE_STATUS_FOR_WORK = { completed: "completed", not_completed: "sent", unknown: "draft" };

export const quoteStatusForWork = (workStatus) => QUOTE_STATUS_FOR_WORK[workStatus] || "";

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** ISO date the parse found on the paper, or "" (the field stays empty). */
export const paperDateFromParse = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
};

/** "" (empty, fine) · "YYYY-MM-DD" · null when it is not a real date or is in the future. */
export const ticketDateIso = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (d.getFullYear() !== Number(m[1]) || d.getMonth() !== Number(m[2]) - 1 || d.getDate() !== Number(m[3])) return null;
  if (text > todayIso()) return null; // a ticket isn't dated in the future
  return text;
};

export const maxTicketDate = todayIso;

/** One sentence for why Save is off, or "" when it can save. */
export const saveBlockedWhy = ({ workStatus, ticketDate }) => {
  if (!workStatus) return "Choose a status — Completed, Not completed or Unknown — to save.";
  if (ticketDateIso(ticketDate) === null) return "Fix the date on the ticket (MM/DD/YYYY) or clear it.";
  return "";
};

/** The fields the save needs from the review, plus the reviewer's 409 answer. */
export const truthFields = ({ workStatus, ticketDate, choice }) => ({
  work_status: workStatus,
  status: quoteStatusForWork(workStatus),
  document_date: ticketDateIso(ticketDate) || null,
  ...(choice?.customer_decision ? { customer_decision: choice.customer_decision } : {}),
  ...(choice?.customer_decision === "merge" ? { customer_id: choice.customer_id } : {}),
});

const errorBody = (err) => err?.response?.data ?? err?.payload ?? {};
const errorStatus = (err) => err?.response?.status ?? err?.status;

/** The 409's choice (detail + existing records), or null for any other error. */
export const customerDecisionFrom = (err) => {
  const body = errorBody(err);
  if (errorStatus(err) !== 409 || body?.code !== "customer_decision_required") return null;
  return {
    detail: String(body?.detail || "This phone or email belongs to an existing customer at a different address."),
    basis: String(body?.basis || ""),
    existing: Array.isArray(body?.existing) ? body.existing : [],
  };
};

// The review endpoint's refusals (revival/scan_review.py). `final` means the same
// answer can never succeed on this quote, so the row is closed, not retried.
const REVIEW_REFUSALS = {
  status_moved_past_scan: { final: true, message: "Moved past the scan (accepted, converted or rejected). Not changed." },
  signed_or_paid: { final: true, message: "Signed or paid. Not changed here." },
  not_a_scanned_quote: { final: true, message: "Not a scanned quote." },
  already_decided: { final: true, message: "Someone already decided this customer. Reload to see it." },
  no_customer_conflict: { final: true, message: "No longer a different-address match. Nothing saved." },
  customer_mismatch: { final: true, message: "This quote's customer changed since it loaded. Nothing saved — reload." },
};

/** A refused review: {code, final, message}. */
export const reviewRefusal = (err) => {
  const body = errorBody(err);
  const known = REVIEW_REFUSALS[body?.code];
  if (known) return { code: body.code, ...known };
  if (errorStatus(err) === 403) {
    return { code: "forbidden", final: true, message: "Only an owner, a manager or whoever scanned it can review this quote." };
  }
  return { code: String(body?.code || ""), final: false, message: saveErrorMessage(err) };
};

/** What a failed save says to the reviewer. */
export const saveErrorMessage = (err) => {
  const body = errorBody(err);
  if (body?.code === "work_status_required") return "Choose a status before saving.";
  return String(body?.detail || err?.message || "Save failed");
};
